import React from 'react';

// CSS for custom animations - injected once
const LOADING_ANIMATIONS_CSS = `
  @keyframes shimmer-wave {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(200%); }
  }
  @keyframes skeleton-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  @keyframes skeleton-breathe {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }
  @keyframes text-shimmer {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  @keyframes dot-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.9); }
  }
  @keyframes row-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

// Inject styles once
let stylesInjected = false;
const injectStyles = () => {
  if (!stylesInjected && typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = LOADING_ANIMATIONS_CSS;
    document.head.appendChild(styleElement);
    stylesInjected = true;
  }
};
injectStyles();

// ============================================================================
// Skeleton Shimmer - For table-level loading
// ============================================================================
export const SkeletonShimmer: React.FC = () => (
  <div
    className="relative w-3/4 h-4 bg-gray-200 rounded overflow-hidden"
    style={{ animation: 'skeleton-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
  >
    <div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent"
      style={{ animation: 'shimmer-wave 2s ease-in-out infinite' }}
    />
  </div>
);

// ============================================================================
// AI Status Loader - For cell-level loading (enrichment/API calls)
// ============================================================================
interface AIStatusLoaderProps {
  syncKey?: string;
  text?: string;
}

export const AIStatusLoader: React.FC<AIStatusLoaderProps> = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '8px',
      height: '24px',
      width: '100%',
      maxWidth: '140px',
    }}
  >
    {/* Animated shimmer bar */}
    <div
      style={{
        position: 'relative',
        flex: 1,
        height: '8px',
        backgroundColor: '#F3F4F6',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      {/* Flowing gradient animation */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, #F3F4F6 0%, #E0E7FF 25%, #C7D2FE 50%, #E0E7FF 75%, #F3F4F6 100%)',
          backgroundSize: '200% 100%',
          animation: 'ai-shimmer-flow 1.5s ease-in-out infinite',
          borderRadius: '4px',
        }}
      />
    </div>
    {/* Animated dots */}
    <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
      <span
        style={{
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: '#818CF8',
          animation: 'ai-dot-bounce 1.4s ease-in-out infinite',
        }}
      />
      <span
        style={{
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: '#818CF8',
          animation: 'ai-dot-bounce 1.4s ease-in-out infinite',
          animationDelay: '0.2s',
        }}
      />
      <span
        style={{
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: '#818CF8',
          animation: 'ai-dot-bounce 1.4s ease-in-out infinite',
          animationDelay: '0.4s',
        }}
      />
    </div>
    <style>{`
      @keyframes ai-shimmer-flow {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes ai-dot-bounce {
        0%, 80%, 100% {
          transform: scale(0.8);
          opacity: 0.5;
        }
        40% {
          transform: scale(1.2);
          opacity: 1;
        }
      }
    `}</style>
  </div>
);

// ============================================================================
// Shimmer Bar - Elegant shimmer bar for skeleton loading
// ============================================================================
interface ShimmerBarProps {
  width?: string;
  delay?: number;
}

const ShimmerBar: React.FC<ShimmerBarProps> = ({ width = '70%', delay = 0 }) => (
  <div
    className="relative h-3 bg-gray-100 rounded overflow-hidden"
    style={{
      width,
      animation: 'skeleton-breathe 2s ease-in-out infinite',
      animationDelay: `${delay}ms`,
    }}
  >
    <div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
      style={{
        animation: 'shimmer-wave 2s ease-in-out infinite',
        animationDelay: `${delay}ms`,
      }}
    />
  </div>
);

// ============================================================================
// Row Loading Skeleton - For table rows loading state
// ============================================================================
interface ColumnConfig {
  width: number;
  key: string;
  name: string;
}

interface RowLoadingSkeletonProps {
  rows?: number;
  columns?: number | ColumnConfig[];
}

export const RowLoadingSkeleton: React.FC<RowLoadingSkeletonProps> = ({
  rows = 12,
  columns = 5
}) => {
  const columnConfigs: ColumnConfig[] = typeof columns === 'number'
    ? Array.from({ length: columns }, (_, i) => ({
        width: 200,
        key: `col-${i}`,
        name: `Column ${i}`
      }))
    : columns;

  const getBarWidth = (colIndex: number, rowIndex: number) => {
    const widths = ['60%', '75%', '55%', '80%', '65%', '70%', '50%', '85%'];
    return widths[(colIndex + rowIndex) % widths.length];
  };

  const totalWidth = columnConfigs.reduce((total, col) => total + col.width, 0) + 40 + 48;

  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex border-b border-gray-100 bg-white pointer-events-none"
          style={{
            width: `${totalWidth}px`,
            animation: 'row-fade-in 0.3s ease-out forwards',
            animationDelay: `${rowIndex * 50}ms`,
            opacity: 0,
          }}
        >
          {/* Row number column */}
          <div className="flex items-center justify-center w-10 min-w-[40px] max-w-[40px] border-r border-gray-100 bg-gray-50 sticky left-0 z-[3]">
            <div className="w-4 h-2.5 bg-gray-200 rounded-sm opacity-50" />
          </div>

          {/* Data columns */}
          {columnConfigs.map((column, colIndex) => (
            <div
              key={column.key}
              className={`relative flex items-center px-4 py-2.5 border-r border-gray-100 min-h-[38px] ${
                colIndex === 0
                  ? 'bg-gray-50 sticky left-10 z-[2] shadow-[2px_0_4px_rgba(0,0,0,0.03)]'
                  : 'bg-white'
              }`}
              style={{
                width: `${column.width}px`,
                minWidth: `${column.width}px`,
                maxWidth: `${column.width}px`,
              }}
            >
              <ShimmerBar
                width={getBarWidth(colIndex, rowIndex)}
                delay={(rowIndex * 50) + (colIndex * 30)}
              />
            </div>
          ))}

          {/* Action column spacer */}
          <div className="relative bg-white flex-shrink-0 w-12 min-w-[48px] max-w-[48px]" />
        </div>
      ))}
    </>
  );
};

// ============================================================================
// Cell Shimmer - Alias for AIStatusLoader
// ============================================================================
interface CellShimmerProps {
  text?: string;
}

export const CellShimmer: React.FC<CellShimmerProps> = () => (
  <AIStatusLoader />
);

// Full Page Loader
interface FullPageLoaderProps {
  message?: string;
  subMessage?: string;
}

// Palette mirrors the portal's circle theme (src/portal/theme/_colors.css:
// paper-0 / line-1/2 / green-600 / ink-900 / ink-500) so the auth-time loader
// and PortalLoader read as one design, not a gray→green flash. Hex on purpose:
// this renders before any theme CSS exists in the document.
export const FullPageLoader: React.FC<FullPageLoaderProps> = ({
  message = 'Just a moment',
  subMessage = 'Setting things up for you...'
}) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#FCFBF8]">
      <div className="flex flex-col items-center">
        {/* Minimal animated loader */}
        <div className="relative mb-8">
          {/* Outer ring */}
          <div
            className="w-14 h-14 rounded-full border-2 border-[#E5E2D9]"
            style={{
              animation: 'skeleton-breathe 2s ease-in-out infinite',
            }}
          />
          {/* Spinning arc */}
          <div
            className="absolute inset-0 w-14 h-14 rounded-full border-2 border-transparent border-t-[#196449]"
            style={{
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>

        {/* Text content - serif heading */}
        <h2 className="text-[22px] font-serif font-normal text-[#10201A] mb-2 tracking-tight">
          {message}
        </h2>
        <p className="text-[14px] text-[#5C6B63]">
          {subMessage}
        </p>

        {/* Subtle progress dots */}
        <div className="flex items-center gap-1.5 mt-6">
          <span
            className="w-1 h-1 rounded-full bg-[#D5D1C5]"
            style={{ animation: 'dot-pulse 1.5s ease-in-out infinite' }}
          />
          <span
            className="w-1 h-1 rounded-full bg-[#D5D1C5]"
            style={{ animation: 'dot-pulse 1.5s ease-in-out infinite', animationDelay: '200ms' }}
          />
          <span
            className="w-1 h-1 rounded-full bg-[#D5D1C5]"
            style={{ animation: 'dot-pulse 1.5s ease-in-out infinite', animationDelay: '400ms' }}
          />
        </div>
      </div>
    </div>
  );
};